import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Plus, X } from 'lucide-react'

import { Dialog, DialogClose, DialogContent, DialogDescription, DialogTitle } from '../components/ui/dialog'

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card'
import { Input } from '../components/ui/input'
import { Button } from '../components/ui/button'
import { DashboardUser, createDashboardUser, deleteDashboardUser, getDashboardUsers, sendDashboardUserPasswordReset, setUserDisabledStatus, updateDashboardUserPreferences, updateUserRole } from '../lib/api/adminUsers'
import { useAuth } from '../hooks/useAuth'
import { getTourOperators, type TourOperator } from '../lib/api/tourOperators'
import type { DashboardUserRole } from '../lib/api/auth'
import type { MeasurementUnit } from '../lib/measurements'

const sortOptions = [
  { label: 'Recently active', value: 'recent' },
  { label: 'Oldest accounts', value: 'oldest' },
  { label: 'Alphabetical', value: 'name' },
  { label: 'Role', value: 'role' }
] as const

const roleFilters: RoleFilter[] = ['all', 'Admin', 'Editor', 'TourAdmin', 'SupportAdmin', 'SupportAgent', 'Guest']

type SortValue = (typeof sortOptions)[number]['value']
type RoleFilter = 'all' | Extract<DashboardUserRole, 'Admin' | 'Editor' | 'TourAdmin' | 'SupportAdmin' | 'SupportAgent' | 'Guest'>
type CreatableRole = Exclude<RoleFilter, 'all' | 'Guest'> // Roles that can be assigned when creating users
type AssignableRole = Exclude<RoleFilter, 'all'> // Roles that can be assigned when editing users (includes Guest)

const formatDate = (value?: string | null) => {
  if (!value) {
    return '—'
  }
  try {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    }).format(new Date(value))
  } catch (error) {
    console.warn('Failed to format date', error)
    return value
  }
}

const getInitials = (name?: string | null, fallback?: string) => {
  if (name && name.trim().length > 0) {
    return name
      .trim()
      .split(' ')
      .slice(0, 2)
      .map((piece) => piece.charAt(0).toUpperCase())
      .join('')
  }
  if (fallback) {
    return fallback.substring(0, 2).toUpperCase()
  }
  return 'HS'
}

const roleBadgeClasses = (role: DashboardUserRole) => {
  switch (role) {
    case 'Admin':
      return 'bg-emerald-100 text-emerald-800'
    case 'TourAdmin':
      return 'bg-amber-100 text-amber-800'
    case 'SupportAdmin':
      return 'bg-purple-100 text-purple-800'
    case 'SupportAgent':
      return 'bg-blue-100 text-blue-800'
    case 'Guest':
      return 'bg-orange-100 text-orange-800'
    case 'Editor':
    default:
      return 'bg-slate-100 text-slate-700'
  }
}

export const DashboardUsersPage = () => {
  const { user: authUser } = useAuth()
  const [users, setUsers] = useState<DashboardUser[]>([])
  const [filteredRole, setFilteredRole] = useState<RoleFilter>('all')
  const [sortBy, setSortBy] = useState<SortValue>('recent')
  const [searchTerm, setSearchTerm] = useState('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isInviteOpen, setIsInviteOpen] = useState(false)
  const inviteButtonRef = useRef<HTMLButtonElement>(null)
  const [inviteError, setInviteError] = useState<string | null>(null)
  const [inviteSuccess, setInviteSuccess] = useState<string | null>(null)
  const [inviteLoading, setInviteLoading] = useState(false)
  const [inviteForm, setInviteForm] = useState({
    email: '',
    displayName: '',
    password: '',
    role: 'Editor' as CreatableRole,
    operatorId: ''
  })
  const [operators, setOperators] = useState<TourOperator[]>([])
  // Load once per attempt; an empty or failed result must not re-trigger the effect (BH-15).
  const [operatorsStatus, setOperatorsStatus] = useState<'idle' | 'loading' | 'loaded' | 'failed'>('idle')
  const operatorsLoading = operatorsStatus === 'loading'
  const [disablingUserId, setDisablingUserId] = useState<string | null>(null)
  const [savingMeasurementUserId, setSavingMeasurementUserId] = useState<string | null>(null)
  const [measurementUnitError, setMeasurementUnitError] = useState<string | null>(null)
  const [editingRoleUserId, setEditingRoleUserId] = useState<string | null>(null)
  const [roleEditForm, setRoleEditForm] = useState<{ role: AssignableRole; operatorId: string }>({ role: 'Editor', operatorId: '' })
  const [roleEditError, setRoleEditError] = useState<string | null>(null)
  const [savingRole, setSavingRole] = useState(false)
  const [deletingUserId, setDeletingUserId] = useState<string | null>(null)
  const [confirmDeleteUserId, setConfirmDeleteUserId] = useState<string | null>(null)
  const [resetPasswordUserId, setResetPasswordUserId] = useState<string | null>(null)
  const [resetPasswordError, setResetPasswordError] = useState<string | null>(null)
  const [resetPasswordSuccess, setResetPasswordSuccess] = useState<string | null>(null)
  const [savingPassword, setSavingPassword] = useState(false)
  const callerRole = (authUser?.role ?? 'Guest') as DashboardUserRole

  const loadUsers = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await getDashboardUsers()
      setUsers(data)
    } catch (err) {
      console.error('Failed to load dashboard users', err)
      setError('Unable to load dashboard users right now.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void loadUsers()
  }, [loadUsers])

  const creatableRoles = useMemo<CreatableRole[]>(() => {
    if (callerRole === 'Admin') {
      return ['Admin', 'SupportAdmin', 'Editor', 'SupportAgent', 'TourAdmin']
    }
    if (callerRole === 'SupportAdmin') {
      return ['SupportAgent', 'TourAdmin']
    }
    if (callerRole === 'Editor' || callerRole === 'SupportAgent') {
      return ['TourAdmin']
    }
    return []
  }, [callerRole])

  const shouldLoadOperators = useMemo(
    () => creatableRoles.includes('TourAdmin') || users.some((user) => user.role === 'TourAdmin'),
    [creatableRoles, users]
  )

  useEffect(() => {
    if (!shouldLoadOperators || operatorsStatus !== 'idle') {
      return
    }

    setOperatorsStatus('loading')
    void getTourOperators()
      .then((data) => {
        setOperators(data)
        setOperatorsStatus('loaded')
      })
      .catch((err) => {
        console.error('Failed to load tour operators', err)
        setOperatorsStatus('failed')
      })
  }, [operatorsStatus, shouldLoadOperators])

  useEffect(() => {
    if (inviteForm.role === 'TourAdmin' && !inviteForm.operatorId && operators.length > 0) {
      setInviteForm((prev) => ({ ...prev, operatorId: operators[0].id }))
    }
  }, [inviteForm.operatorId, inviteForm.role, operators])

  const operatorLookup = useMemo(() => {
    const map = new Map<string, string>()
    operators.forEach((op) => map.set(op.id, op.name))
    return map
  }, [operators])

  useEffect(() => {
    if (creatableRoles.length === 0) {
      return
    }

    if (!creatableRoles.includes(inviteForm.role)) {
      setInviteForm((prev) => ({ ...prev, role: creatableRoles[0] }))
    }
  }, [creatableRoles, inviteForm.role])

  const handleInviteSubmit = useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault()
      if (!inviteForm.email || !inviteForm.password) {
        return
      }

      if (inviteForm.role === 'TourAdmin' && !inviteForm.operatorId) {
        setInviteError('Select an operator for TourAdmin accounts.')
        return
      }

      setInviteError(null)
      setInviteSuccess(null)
      setInviteLoading(true)
      try {
        await createDashboardUser({
          email: inviteForm.email.trim(),
          password: inviteForm.password,
          displayName: inviteForm.displayName?.trim() || undefined,
          role: inviteForm.role,
          operatorId: inviteForm.role === 'TourAdmin' ? inviteForm.operatorId : undefined
        })
        setInviteSuccess('Dashboard user created successfully.')
        setInviteForm((prev) => ({
          ...prev,
          email: '',
          displayName: '',
          password: '',
          operatorId: prev.role === 'TourAdmin' ? prev.operatorId : ''
        }))
        await loadUsers()
      } catch (err: unknown) {
        const message =
          err && typeof err === 'object' && 'response' in err
            ? // @ts-expect-error axios error shape
              err.response?.data?.message ?? 'Failed to create user.'
            : 'Failed to create user.'
        setInviteError(message)
      } finally {
        setInviteLoading(false)
      }
    },
    [inviteForm, loadUsers]
  )

  const inviteDisabled =
    !inviteForm.email ||
    !inviteForm.password ||
    inviteLoading ||
    (inviteForm.role === 'TourAdmin' && !inviteForm.operatorId)

  const handleToggleDisabled = useCallback(
    async (user: DashboardUser) => {
      if (disablingUserId) return
      if (user.id === authUser?.id) return

      setDisablingUserId(user.id)
      try {
        await setUserDisabledStatus(user.id, !user.isDisabled)
        await loadUsers()
      } catch (err) {
        console.error('Failed to toggle user disabled status', err)
      } finally {
        setDisablingUserId(null)
      }
    },
    [authUser?.id, disablingUserId, loadUsers]
  )

  const handleMeasurementUnitChange = useCallback(
    async (targetUser: DashboardUser, measurementUnit: MeasurementUnit) => {
      if (callerRole !== 'Admin' || targetUser.id === authUser?.id || savingMeasurementUserId) {
        return
      }

      setSavingMeasurementUserId(targetUser.id)
      setMeasurementUnitError(null)
      try {
        const updated = await updateDashboardUserPreferences(targetUser.id, { measurementUnit })
        setUsers((prev) => prev.map((user) => (user.id === updated.id ? updated : user)))
      } catch (err) {
        console.error('Failed to update dashboard user measurement unit', err)
        setMeasurementUnitError('Could not update measurement preference. Please try again.')
      } finally {
        setSavingMeasurementUserId(null)
      }
    },
    [authUser?.id, callerRole, savingMeasurementUserId]
  )

  const handleStartEditRole = useCallback((user: DashboardUser) => {
    setEditingRoleUserId(user.id)
    setRoleEditForm({
      role: user.role as AssignableRole,
      operatorId: user.operatorId ?? ''
    })
    setRoleEditError(null)
  }, [])

  const handleCancelEditRole = useCallback(() => {
    setEditingRoleUserId(null)
    setRoleEditForm({ role: 'Editor', operatorId: '' })
    setRoleEditError(null)
  }, [])

  const handleSaveRole = useCallback(async () => {
    if (!editingRoleUserId || savingRole) return

    if (roleEditForm.role === 'TourAdmin' && !roleEditForm.operatorId) {
      setRoleEditError('TourAdmin requires an operator.')
      return
    }

    setSavingRole(true)
    setRoleEditError(null)
    try {
      await updateUserRole(editingRoleUserId, {
        role: roleEditForm.role,
        operatorId: roleEditForm.role === 'TourAdmin' ? roleEditForm.operatorId : null
      })
      setEditingRoleUserId(null)
      await loadUsers()
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'response' in err
          ? // @ts-expect-error axios error shape
            err.response?.data?.message ?? 'Failed to update role.'
          : 'Failed to update role.'
      setRoleEditError(message)
    } finally {
      setSavingRole(false)
    }
  }, [editingRoleUserId, loadUsers, roleEditForm, savingRole])

  const handleDeleteUser = useCallback(async (userId: string) => {
    setDeletingUserId(userId)
    try {
      await deleteDashboardUser(userId)
      setConfirmDeleteUserId(null)
      await loadUsers()
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'response' in err
          ? // @ts-expect-error axios error shape
            err.response?.data?.message ?? 'Failed to delete user.'
          : 'Failed to delete user.'
      alert(message)
    } finally {
      setDeletingUserId(null)
    }
  }, [loadUsers])

  const handleResetPassword = useCallback(async () => {
    if (!resetPasswordUserId || savingPassword) return

    setSavingPassword(true)
    setResetPasswordError(null)
    setResetPasswordSuccess(null)
    try {
      await sendDashboardUserPasswordReset(resetPasswordUserId)
      setResetPasswordSuccess('Password reset email sent.')
      setResetPasswordUserId(null)
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'response' in err
          ? // @ts-expect-error axios error shape
            err.response?.data?.message ?? 'Failed to send password reset email.'
          : 'Failed to send password reset email.'
      setResetPasswordError(message)
    } finally {
      setSavingPassword(false)
    }
  }, [resetPasswordUserId, savingPassword])

  const stats = useMemo(() => {
    const admins = users.filter((user) => user.role === 'Admin').length
    const editors = users.filter((user) => user.role === 'Editor').length
    const tourAdmins = users.filter((user) => user.role === 'TourAdmin').length
    const supportAdmins = users.filter((user) => user.role === 'SupportAdmin').length
    const supportAgents = users.filter((user) => user.role === 'SupportAgent').length
    const guests = users.filter((user) => user.role === 'Guest').length
    const lastUpdated = users.reduce<string | null>((latest, user) => {
      const candidate = user.updatedAt ?? user.createdAt
      if (!candidate) {
        return latest
      }
      if (!latest) {
        return candidate
      }
      return new Date(candidate) > new Date(latest) ? candidate : latest
    }, null)

    return {
      total: users.length,
      admins,
      editors,
      tourAdmins,
      supportAdmins,
      supportAgents,
      guests,
      lastUpdated: lastUpdated ? formatDate(lastUpdated) : '—'
    }
  }, [users])

  const visibleUsers = useMemo(() => {
    const normalizedQuery = searchTerm.trim().toLowerCase()

    const filtered = users.filter((user) => {
      const matchesRole = filteredRole === 'all' || user.role === filteredRole
      const matchesQuery =
        normalizedQuery.length === 0 ||
        user.email.toLowerCase().includes(normalizedQuery) ||
        (user.displayName ?? '').toLowerCase().includes(normalizedQuery)
      return matchesRole && matchesQuery
    })

    return filtered.sort((a, b) => {
      switch (sortBy) {
        case 'oldest':
          return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
        case 'name':
          return (a.displayName ?? a.email).localeCompare(b.displayName ?? b.email)
        case 'role':
          return a.role.localeCompare(b.role)
        case 'recent':
        default:
          return new Date(b.lastLoginAt ?? b.updatedAt).getTime() - new Date(a.lastLoginAt ?? a.updatedAt).getTime()
      }
    })
  }, [users, filteredRole, searchTerm, sortBy])

  return (
    <div className="space-y-6">
      <header className="workspace-heading">
        <div>
          <h1>Dashboard users</h1>
          <p>Manage your team’s access and permissions.</p>
        </div>
        {creatableRoles.length > 0 && <Button ref={inviteButtonRef} onClick={() => setIsInviteOpen(true)} className="gap-2"><Plus className="h-4 w-4" />Invite teammate</Button>}
      </header>

      <dl aria-label="Account summary" className="workspace-summary grid-cols-2 sm:grid-cols-4 xl:grid-cols-7">
        {[
          ['Total accounts', stats.total], ['Admins', stats.admins], ['Editors', stats.editors],
          ['Tour Admins', stats.tourAdmins], ['Support Admins', stats.supportAdmins],
          ['Support Agents', stats.supportAgents], ['Guests', stats.guests]
        ].map(([label, value]) => <div key={label} className="px-1"><dt>{label}</dt><dd>{loading || error ? '—' : value}</dd></div>)}
      </dl>

      <Dialog open={isInviteOpen && creatableRoles.length > 0} onOpenChange={setIsInviteOpen}>
      <DialogContent className="w-[min(94vw,640px)]" onCloseAutoFocus={(event) => { event.preventDefault(); inviteButtonRef.current?.focus() }}>
      <DialogClose asChild><button type="button" aria-label="Close invitation" className="absolute right-3 top-3 rounded-md p-1.5 text-muted-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"><X className="h-4 w-4" /></button></DialogClose>
        <CardHeader className="pb-3 pr-12">
          <DialogTitle>Invite dashboard teammate</DialogTitle>
          <DialogDescription>
            Create an allowlisted dashboard account. Use their Google email to enable Google sign-in; the temporary password remains a fallback. You can only assign roles up to your own access level.
          </DialogDescription>
        </CardHeader>
        <CardContent>
          <form className="space-y-4" onSubmit={handleInviteSubmit}>
            <div className="grid gap-4 md:grid-cols-2">
              <Input
                type="email"
                aria-label="Email address" placeholder="Email address"
                value={inviteForm.email}
                onChange={(event) => setInviteForm((prev) => ({ ...prev, email: event.target.value }))}
                required
              />
              <Input
                type="text"
                aria-label="Display name (optional)" placeholder="Display name (optional)"
                value={inviteForm.displayName}
                onChange={(event) => setInviteForm((prev) => ({ ...prev, displayName: event.target.value }))}
              />
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <Input
                type="password"
                aria-label="Temporary password" placeholder="Temporary password"
                value={inviteForm.password}
                onChange={(event) => setInviteForm((prev) => ({ ...prev, password: event.target.value }))}
                required
                minLength={6}
              />
              <label className="text-sm font-medium text-muted-foreground">
                Role
                <select
                  className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground"
                  value={inviteForm.role}
                  onChange={(event) => setInviteForm((prev) => ({ ...prev, role: event.target.value as CreatableRole }))}
                >
                  {creatableRoles.map((role) => (
                    <option key={role} value={role}>
                      {role}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            {inviteForm.role === 'TourAdmin' && (
              <label className="text-sm font-medium text-muted-foreground">
                Tour operator
                <select
                  className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground"
                  value={inviteForm.operatorId}
                  onChange={(event) => setInviteForm((prev) => ({ ...prev, operatorId: event.target.value }))}
                  disabled={operatorsLoading || operators.length === 0}
                >
                  <option value="">Select operator…</option>
                  {operators.map((op) => (
                    <option key={op.id} value={op.id}>
                      {op.name}
                    </option>
                  ))}
                </select>
                {operatorsLoading && <p className="mt-1 text-xs text-muted-foreground">Loading operators…</p>}
                {operatorsStatus === 'loaded' && operators.length === 0 && (
                  <p className="mt-1 text-xs text-muted-foreground">No operators available yet.</p>
                )}
                {operatorsStatus === 'failed' && (
                  <span className="mt-1 flex items-center gap-2 text-xs text-red-500">
                    Failed to load operators.
                    <Button type="button" variant="outline" size="sm" onClick={() => setOperatorsStatus('idle')}>
                      Retry
                    </Button>
                  </span>
                )}
              </label>
            )}
            {inviteError && <p className="text-sm text-red-500">{inviteError}</p>}
            {inviteSuccess && <p className="text-sm text-emerald-600">{inviteSuccess}</p>}
            <div className="flex justify-end">
              <Button type="submit" disabled={inviteDisabled}>
                {inviteLoading ? 'Creating…' : 'Create account'}
              </Button>
            </div>
          </form>
        </CardContent>
      </DialogContent>
      </Dialog>

      <Card>
        <CardHeader className="pb-4">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <CardTitle>People directory</CardTitle>
              <CardDescription>Last account update: {stats.lastUpdated}.</CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={() => loadUsers()} disabled={loading}>
              Refresh list
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
            <Input aria-label="Search dashboard users" placeholder="Search by name or email" value={searchTerm} onChange={(event) => setSearchTerm(event.target.value)} className="lg:w-72" />

            <div className="flex min-w-0 flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
              <div role="group" aria-label="Filter dashboard users by role" className="flex flex-wrap gap-1 rounded-lg bg-muted/60 p-1 text-xs font-medium">
                {roleFilters.map((role) => (
                  <button
                    key={role}
                    type="button"
                    onClick={() => setFilteredRole(role)}
                    aria-pressed={filteredRole === role}
                    className={`rounded-md px-2.5 py-1.5 transition-colors ${filteredRole === role ? 'bg-card text-primary shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
                  >
                    {role === 'all' ? 'All roles' : role}
                  </button>
                ))}
              </div>
              <label className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
                Sort by
                <select
                  value={sortBy}
                  onChange={(event) => setSortBy(event.target.value as SortValue)}
                  className="rounded-lg border border-border bg-background px-3 py-2 text-sm"
                >
                  {sortOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          </div>

          {loading && <p className="text-sm text-muted-foreground">Loading dashboard users…</p>}
          {error && <p className="text-sm text-red-500">{error}</p>}

          <div className="overflow-x-auto">
            <table className="workspace-table min-w-full text-left text-sm">
              <thead>
                <tr className="border-b border-border text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="py-2 pr-4 font-medium">User</th>
                  <th className="py-2 pr-4 font-medium">Role</th>
                  <th className="py-2 pr-4 font-medium">Units</th>
                  <th className="py-2 pr-4 font-medium">Status</th>
                  <th className="py-2 pr-4 font-medium">Last login</th>
                  <th className="py-2 pr-4 font-medium">Created</th>
                  {callerRole === 'Admin' && <th className="py-2 pr-4 font-medium">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {visibleUsers.map((user) => (
                  <tr key={user.id} className="border-b border-border/60 last:border-b-0">
                    <td className="py-3 pr-4">
                      <div className="flex items-center gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold uppercase text-primary">
                          {getInitials(user.displayName, user.email)}
                        </div>
                        <div>
                          <p className="font-medium text-foreground">{user.displayName ?? 'Unnamed account'}</p>
                          <p className="text-xs text-muted-foreground">{user.email}</p>
                        </div>
                      </div>
                </td>
                <td className="py-3 pr-4">
                  {callerRole === 'Admin' && user.id !== authUser?.id && editingRoleUserId === user.id ? (
                    <div className="space-y-2">
                      <select
                        className="w-full rounded-md border border-border bg-background px-2 py-1 text-sm"
                        value={roleEditForm.role}
                        onChange={(e) => setRoleEditForm((prev) => ({ ...prev, role: e.target.value as AssignableRole }))}
                        disabled={savingRole}
                      >
                        <option value="Admin">Admin</option>
                        <option value="SupportAdmin">SupportAdmin</option>
                        <option value="Editor">Editor</option>
                        <option value="SupportAgent">SupportAgent</option>
                        <option value="TourAdmin">TourAdmin</option>
                        <option value="Guest">Guest</option>
                      </select>
                      {roleEditForm.role === 'TourAdmin' && (
                        <select
                          className="w-full rounded-md border border-border bg-background px-2 py-1 text-sm"
                          value={roleEditForm.operatorId}
                          onChange={(e) => setRoleEditForm((prev) => ({ ...prev, operatorId: e.target.value }))}
                          disabled={savingRole || operatorsLoading}
                        >
                          <option value="">Select operator…</option>
                          {operators.map((op) => (
                            <option key={op.id} value={op.id}>{op.name}</option>
                          ))}
                        </select>
                      )}
                      {roleEditError && <p className="text-xs text-red-500">{roleEditError}</p>}
                      <div className="flex gap-1">
                        <Button size="sm" onClick={handleSaveRole} disabled={savingRole}>
                          {savingRole ? 'Saving…' : 'Save'}
                        </Button>
                        <Button size="sm" variant="ghost" onClick={handleCancelEditRole} disabled={savingRole}>
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    // eslint-disable-next-line jsx-a11y/no-static-element-interactions -- Interactivity is conditionally enabled
                    <div
                      className={callerRole === 'Admin' && user.id !== authUser?.id ? 'cursor-pointer group' : ''}
                      onClick={() => callerRole === 'Admin' && user.id !== authUser?.id && handleStartEditRole(user)}
                      onKeyDown={(e) => {
                        if ((e.key === 'Enter' || e.key === ' ') && callerRole === 'Admin' && user.id !== authUser?.id) {
                          e.preventDefault()
                          handleStartEditRole(user)
                        }
                      }}
                      role={callerRole === 'Admin' && user.id !== authUser?.id ? 'button' : undefined}
                      tabIndex={callerRole === 'Admin' && user.id !== authUser?.id ? 0 : undefined}
                      title={callerRole === 'Admin' && user.id !== authUser?.id ? 'Click to change role' : undefined}
                    >
                      <span className={`rounded-full px-3 py-1 text-xs font-semibold ${roleBadgeClasses(user.role as DashboardUserRole)} ${callerRole === 'Admin' && user.id !== authUser?.id ? 'group-hover:ring-2 group-hover:ring-primary/30' : ''}`}>
                        {user.role}
                      </span>
                      {user.role === 'TourAdmin' && (
                        <p className="mt-1 text-xs text-muted-foreground">
                          {user.operatorId ? operatorLookup.get(user.operatorId) ?? 'Operator access' : 'No operator linked'}
                        </p>
                      )}
                    </div>
                  )}
                </td>
                    <td className="py-3 pr-4">
                      {callerRole === 'Admin' && user.id !== authUser?.id ? (
                        <select
                          aria-label={`Measurement units for ${user.displayName ?? user.email}`}
                          className="w-full min-w-28 rounded-md border border-border bg-background px-2 py-1 text-sm"
                          value={user.measurementUnit ?? 'Metric'}
                          onChange={(event) => void handleMeasurementUnitChange(user, event.target.value as MeasurementUnit)}
                          disabled={savingMeasurementUserId === user.id}
                        >
                          <option value="Metric">Metric</option>
                          <option value="Imperial">Imperial</option>
                        </select>
                      ) : (
                        <span className="rounded-full bg-muted px-3 py-1 text-xs font-semibold text-muted-foreground">
                          {user.measurementUnit ?? 'Metric'}
                        </span>
                      )}
                    </td>
                    <td className="py-3 pr-4">
                      {user.isDisabled ? (
                        <span className="rounded-full bg-red-100 px-3 py-1 text-xs font-semibold text-red-800">Disabled</span>
                      ) : (
                        <span className="rounded-full bg-green-100 px-3 py-1 text-xs font-semibold text-green-800">Active</span>
                      )}
                    </td>
                    <td className="py-3 pr-4 text-muted-foreground">{formatDate(user.lastLoginAt)}</td>
                    <td className="py-3 pr-4 text-muted-foreground">{formatDate(user.createdAt)}</td>
                    {callerRole === 'Admin' && (
                      <td className="py-3 pr-4">
                        {user.id !== authUser?.id && (
                          <div className="flex items-center gap-1">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleToggleDisabled(user)}
                              disabled={disablingUserId === user.id}
                            >
                              {disablingUserId === user.id ? 'Updating…' : user.isDisabled ? 'Enable' : 'Disable'}
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => { setResetPasswordUserId(user.id); setResetPasswordError(null); setResetPasswordSuccess(null) }}
                            >
                              Reset pw
                            </Button>
                            {confirmDeleteUserId === user.id ? (
                              <div className="flex items-center gap-1">
                                <Button
                                  variant="destructive"
                                  size="sm"
                                  onClick={() => handleDeleteUser(user.id)}
                                  disabled={deletingUserId === user.id}
                                >
                                  {deletingUserId === user.id ? 'Deleting…' : 'Confirm'}
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => setConfirmDeleteUserId(null)}
                                  disabled={deletingUserId === user.id}
                                >
                                  Cancel
                                </Button>
                              </div>
                            ) : (
                              <Button
                                variant="outline"
                                size="sm"
                                className="text-red-600 hover:text-red-700"
                                onClick={() => setConfirmDeleteUserId(user.id)}
                              >
                                Delete
                              </Button>
                            )}
                          </div>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
                {!loading && visibleUsers.length === 0 && (
                  <tr>
                    <td colSpan={callerRole === 'Admin' ? 7 : 6} className="py-6 text-center text-sm text-muted-foreground">
                      No users match your filters.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {measurementUnitError && <p className="text-sm text-red-500">{measurementUnitError}</p>}
          {resetPasswordSuccess && <p className="text-sm text-emerald-600">{resetPasswordSuccess}</p>}
        </CardContent>
      </Card>

      {resetPasswordUserId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle>Reset password</CardTitle>
              <CardDescription>
                Send a password reset link to{' '}
                <span className="font-medium text-foreground">
                  {users.find((u) => u.id === resetPasswordUserId)?.email ?? 'this user'}
                </span>.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {resetPasswordError && <p className="text-sm text-red-500">{resetPasswordError}</p>}
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => { setResetPasswordUserId(null); setResetPasswordError(null) }} disabled={savingPassword}>
                  Cancel
                </Button>
                <Button onClick={handleResetPassword} disabled={savingPassword}>
                  {savingPassword ? 'Sending...' : 'Send reset email'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  )
}
